import { Middleware } from '@reduxjs/toolkit'
import { AUTH_TOKEN, hasSessionOverride } from 'setup/SetupAxios'
import type { RootState } from '../index'
import { logout, mapNylasEmailsToExtensionFormat, patchApplication, selectApplication, selectUser, setApplication, setUser } from '../authSlice'

// Single owner of reactive auth→extension sync.
//
// Why this exists: `useAuth` (= useAuthRedux) is mounted in ~308 components and the
// `setApplication` reducer used to fire its own
// `JSON.stringify` + `window.postMessage` on every socket-driven update. One application
// reference change therefore fanned out into hundreds of full-payload serializations — the prod
// Dispatch Planner trace showed ~9.4 s of JSON.stringify, the Update Board ~1.9 s of postMessage.
//
// A Redux middleware runs ONCE per dispatch, after the reducer, off the React render path. So the
// fan-out collapses to a single serialization per auth action, content-diffed so no-op socket
// churn (sanitizeApplication hands back a fresh-but-equal object every tick) costs nothing. This
// replaces both the per-consumer effect and the in-reducer side-effects — reducers stay pure.

// The set of action types whose result can change what the extension should hold. `logout` is
// included so the diff resets when the user signs out (the next sign-in then always re-syncs).
const SYNCED_ACTIONS = new Set<string>([setUser.type, setApplication.type, patchApplication.type, logout.type])

// Last sent state: a cheap signature of the fields the extension holds.
let lastSig: string | null = null

const extensionPresent = (): boolean =>
    Boolean(localStorage.getItem('numeoExtensionId') || localStorage.getItem('authExtensionId') || localStorage.getItem('numeo-extension-connected') === 'true')

const syncExtension = (state: RootState): void => {
    if (typeof window === 'undefined') return

    const user = selectUser(state)
    // Signed out: reset the diff so the next sign-in always re-syncs. Logout messaging has its own
    // dedicated path (authSlice logout → extensionAuthService.syncAuthState(null)).
    if (!user) {
        lastSig = null
        return
    }

    // Skip entirely while this tab is under a per-tab company-switch override. `user`/`application`
    // here would already reflect the switched company (verifyAuth resolved them via the override
    // token), but the raw read below is always the shared BASE token — pairing the two would push
    // an internally-inconsistent token+company bundle to the extension. The extension deliberately
    // doesn't reflect company switches at all (same as CompanySwitcher never syncing to it); it
    // stays pinned to whatever the base session last synced.
    if (hasSessionOverride()) return

    const token = localStorage.getItem(AUTH_TOKEN)
    if (!token) return

    const application = selectApplication(state)

    const sig = JSON.stringify({
        token,
        id: user.id,
        email: user.email,
        name: user.name,
        emailTemplates: user.emailTemplates,
        nylasEmails: user.nylasEmails,
        integrations: user.integrations,
        application: application
            ? {
                  projectName: application.projectName,
                  features: application.features,
                  integrations: application.integrations,
                  settings: application.settings,
              }
            : null,
    })
    if (sig === lastSig) return
    lastSig = sig

    // Nothing to deliver to if the extension isn't installed/connected — diff is already updated so
    // we won't re-evaluate this snapshot once it does connect (login/connect uses its own handshake).
    if (!extensionPresent()) return

    // (1) EXTENSION_DATA — origin-scoped; mirrors the former useAuthRedux effect.
    const extensionData = {
        token,
        userId: user.id,
        email: user.email,
        name: user.name,
        emailTemplates: user.emailTemplates || [],
        nylasEmails: application?.integrations?.nylas?.emails || [],
        integrations: {},
    }
    window.postMessage({ type: 'EXTENSION_DATA', payload: JSON.stringify(extensionData) }, window.location.origin)

    // (2) NUMEO_AUTH_SUCCESS — mirrors the former setApplication reducer side-effect.
    if (application) {
        window.postMessage(
            {
                type: 'NUMEO_AUTH_SUCCESS',
                user: {
                    token,
                    userId: user.id,
                    email: user.email,
                    name: user.name,
                    emailTemplates: user.emailTemplates || { bodyTemplates: [], footer: '' },
                    nylasEmails: mapNylasEmailsToExtensionFormat(user.nylasEmails),
                    integrations: user.integrations || {},
                    application: {
                        projectName: application.projectName,
                        features: application.features || [],
                        integrations: application.integrations || {},
                        settings: application.settings || {},
                    },
                },
                source: 'numeo-web',
            },
            window.location.origin
        )
    }

    // (3) numeo-auth-changed — consumed by the extension content script and the in-app logout listener.
    window.dispatchEvent(
        new CustomEvent('numeo-auth-changed', {
            detail: {
                token,
                userId: user.id,
                email: user.email,
                name: user.name,
                emailTemplates: user.emailTemplates,
                nylasEmails: user.nylasEmails,
                integrations: user.integrations,
            },
        })
    )
}

export const extensionSyncMiddleware: Middleware = (store) => (next) => (action) => {
    const type = (action as { type?: string })?.type
    const watched = Boolean(type && SYNCED_ACTIONS.has(type))
    // Snapshot the auth slice before the reducer runs. The reducers dedupe no-op updates (isEqual),
    // so a socket tick that didn't actually change anything leaves the same `auth` reference — we can
    // then skip the sync (and its signature build) entirely instead of re-checking every tick.
    const before = watched ? (store.getState() as RootState).auth : null
    const result = next(action)
    if (watched && (store.getState() as RootState).auth !== before) {
        try {
            syncExtension(store.getState() as RootState)
        } catch (error) {
            // postMessage can throw on non-cloneable data — never let it break the dispatch chain.
            console.warn('[extensionSync] Could not sync to extension', error)
        }
    }
    return result
}
